import { Lesson } from './lesson';
import { Base } from './base-model';
import { Type } from 'class-transformer';
import { Song } from './song';
import { LessonSong } from './lesson-song';
import { Common } from '../lib/common'

export class Course extends Base {
    collectionUuid: string
    startDate: {
        year: string,
        month: string,
        day: string
    }
    classType: Course.ClassType
    @Type(() => Lesson)
    lessons: Lesson[]
    isArchived: boolean

    static defaults(): Course {
        const course: Course = new Course()
        const date: Date = new Date()
        
        course.startDate = {
            year: (date.getUTCFullYear()).toString(), 
            month: date.toLocaleString('default', { month: 'short' }), 
            day: (date.getDate()).toString() 
        }
        course.classType = Course.ClassType.None
        course.lessons = []
        course.isArchived = false
        return course
    }

    copyFrom(course: Course) {
        super.copyFrom(course)
        this.collectionUuid = course.collectionUuid
        this.startDate = { year: course.startDate.year, month: course.startDate.month, day: course.startDate.day }
        this.classType = course.classType
        this.lessons.forEach( (lesson: Lesson) => lesson.copyFrom(course.lessons.find((ls: Lesson) => ls.uuid === lesson.uuid)))
        this.lessons = <Lesson[]>Common.matchOrder(this.lessons, course.lessons.map((lesson: Lesson) => lesson.uuid))
        this.isArchived = course.isArchived
    }

    unsavedCopy(): Course {
        const course = super.unsavedCopy() as Course
        course.lessons = course.lessons.map( (lesson: Lesson) => lesson.unsavedCopy() )
        return course
    }

    findLesson(uuid: string): Lesson {
        return this.lessons.find((lesson: Lesson) => uuid && (lesson.uuid === uuid) )
    }

    indexOfLesson(uuid: string): number {
        return this.lessons.findIndex((lesson: Lesson) => uuid && (lesson.uuid === uuid) )
    }

    startDateAsDate(): Date {
        const months: string[] = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
        const monthIndex: number = months.indexOf(this.startDate.month.toLowerCase())

        let dttm: Date = new Date()
        dttm.setFullYear(+this.startDate.year, monthIndex, +this.startDate.day)        
        return dttm
    }

    get displayableLessons(): Lesson[] {
        return this.lessons.filter( (lesson: Lesson) => !lesson.isDeleted )
    }

    uniqueLessonName(name: string): string {
        const getNameRegexp = /(.*) \(copy.*\)/g
        const match = getNameRegexp.exec(name)
        const baseName = (match === null ? name : match[1]).trim()

        let copyNum = 1
        let copyText = ' (copy)'
        while (copyNum < 100) {
            if (this.lessons.find(lesson => lesson.name === (baseName + copyText)) === undefined) {
                return baseName + copyText
            }
            copyText = ' (copy ' + ++copyNum + ')'
        }

        return baseName + copyText
    }

    songUsedCount(song: Song): number {
        let count = 0
        this.lessons.forEach((lesson: Lesson) => {
            if (!lesson.isDeleted) {
                lesson.songs.forEach((lessonSong: LessonSong) => {
                    count += lessonSong.songUuid === song.uuid ? 1 : 0
                })                
            }

        })
        return count
    }

    get getLessonCounts(): number[] {
        switch (this.classType) {
            case Course.ClassType.None:
                return [0]
            case Course.ClassType.InSchool:
                return [7, 8]
            case Course.ClassType.RhythmKidsLevel1:
                return [9]
            case Course.ClassType.RhythmKidsLevel2:
                return [9]
            case Course.ClassType.RhythmKids:
                return [9]
            case Course.ClassType.Babies:
                return [10, 11]
            case Course.ClassType.Generations:
                return [10, 11]
            case Course.ClassType.MixedAges:
                return [12, 14]
            case Course.ClassType.Guitar:
                return [10, 12]
            default:
                return [0]
        }
    }

    equals(other: Course): boolean {
        if (Common.isNullOrUndefined(other)) {
            return false
        }
        if (this.collectionUuid !== other.collectionUuid) {
            return false
        }
        if (this.classType !== other.collectionUuid) {
            return false
        }
        if (this.lessons !== other.lessons) {
            return false
        }
        if (this.isArchived !== other.isArchived) {
            return false
        }
        if (this.startDate !== other.startDate) {
            return false
        }
        if (this.startDate.year !== other.startDate.year) {
            return false
        }
        if (this.startDate.month !== other.startDate.month) {
            return false
        }
        if (this.startDate.day !== other.startDate.day) {
            return false
        }
        return true
    }
}

// https://stackoverflow.com/questions/29844959/enum-inside-class-typescript-definition-file
export namespace Course {
    export enum ClassType {
        None = '',
        InSchool = 'In-School', 
        RhythmKidsLevel1 = 'RK Level 1',
        RhythmKidsLevel2 = 'RK Level 2',
        RhythmKids = 'Rhythm Kids',
        Babies = 'Babies',
        Generations = 'Generations',
        MixedAges = 'Mixed Ages',
        Guitar = 'Guitar'
    }
}

// Note: RhythmKidsLevel1 and RhythmKidsLevel2 have some dependency in app, need to figure out for all used instances.
